import { useState, useRef, type FormEvent } from 'react'
import { Check, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { useAuth } from '../../auth'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tendbot/ui'

const API_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined

type ModalState = 'idle' | 'loading' | 'success' | 'error'

interface Props {
  open: boolean
  onClose: () => void
}

export default function FeedbackModal({ open, onClose }: Props) {
  const { t } = useTranslation('settings')
  const { user } = useAuth()
  const [state, setState] = useState<ModalState>('idle')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const messageRef = useRef<HTMLTextAreaElement>(null)

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onClose()
      return
    }
    setState('idle')
    setEmail(user?.email ?? '')
    setMessage('')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setState('loading')
    // Mirror to Sentry so the feedback gets attached to the current session
    // (replay, breadcrumbs, release, user). Backend POST is the durable record.
    Sentry.captureFeedback(
      { email, message, source: 'feedback-modal' },
      { includeReplay: true },
    )
    try {
      const res = await fetch(`${API_BASE}/leads/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, message }),
      })
      setState(res.ok ? 'success' : 'error')
    } catch {
      setState('error')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        accent="gold"
        className="max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          messageRef.current?.focus()
        }}
      >
        {state === 'success' ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-emerald text-midnight animate-in zoom-in duration-300">
              <Check size={32} />
            </div>
            <h3 className="font-body font-semibold text-ivory mb-2">{t('feedbackModal.successTitle')}</h3>
            <p className="text-pearl text-sm">{t('feedbackModal.successMessage')}</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('feedbackModal.title')}</DialogTitle>
              <DialogDescription>{t('feedbackModal.subtitle')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
              <div className="flex flex-col gap-1">
                <label htmlFor="feedback-email" className="text-sm font-medium text-cream">
                  {t('feedbackModal.emailLabel')}
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="px-4 py-3 bg-midnight/60 border border-slate-mid rounded-lg font-body text-base text-ivory placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 focus:ring-2 focus:ring-gold/10 transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="feedback-message" className="text-sm font-medium text-cream">
                  {t('feedbackModal.messageLabel')}
                </label>
                <textarea
                  ref={messageRef}
                  id="feedback-message"
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('feedbackModal.messagePlaceholder')}
                  rows={4}
                  maxLength={2000}
                  className="px-4 py-3 bg-midnight/60 border border-slate-mid rounded-lg font-body text-base text-ivory placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 focus:ring-2 focus:ring-gold/10 transition-colors resize-y min-h-[100px]"
                />
              </div>
              {state === 'error' && (
                <p className="text-sm text-rose text-center">{t('feedbackModal.error')}</p>
              )}
              <button
                type="submit"
                disabled={state === 'loading'}
                className="w-full mt-2 py-3 rounded-xl bg-gold text-midnight font-semibold text-sm flex items-center justify-center gap-2 hover:bg-gold-light transition-colors disabled:opacity-70 disabled:pointer-events-none"
              >
                {state === 'loading' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-transparent border-t-midnight rounded-full animate-spin" />
                    {t('feedbackModal.submitting')}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {t('feedbackModal.submit')}
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
